import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Button} from '@/components/app/button';
import {Modal} from '@/components/app/modal';
import {selectLimitReached, setLimitReached} from '../demoSlice';
import {Form} from '../../../app/components/Form';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {emailRegExp} from '../../../app/utils/miscUtils';
import {validatePassword} from '../../auth/validatePassword';
import {
  useRegisterMutation,
  useRegisterResendOTPMutation,
  useRegisterVerifyOTPMutation,
} from '../../../app/services/identitiesApi';
import {OTPForm} from '../../auth/components/OTPForm';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {Icon} from '../../../app/components/Icon';
import {getConfigValue} from '../../../app/utils/configUtils';
import {logout} from '../../auth/authSlice';
import {api} from '../../../app/services/api';
enum Step {
  Username = 'username',
  OTP = 'otp',
}
interface FieldValues {
  email: string;
  newPassword: string;
}
export const DemoLimitReachedDialog = () => {
  const navigate = useNavigate();
  const limitReached = useAppSelector(selectLimitReached);
  const [register] = useRegisterMutation();
  const [registerResendOTP] = useRegisterResendOTPMutation();
  const [registerVerifyOTP] = useRegisterVerifyOTPMutation();
  const [username, setUsername] = useState('');
  const [step, setStep] = useState(Step.Username);
  const dispatch = useAppDispatch();
  return (
    <Modal
      className={'w-[30rem]'}
      closable={limitReached?.closable !== false}
      title={limitReached?.title ?? 'Demo Limit Reached'}
      onClose={() => {
        dispatch(setLimitReached(null));
        setStep(Step.Username);
        setUsername('');
      }}
      open={limitReached != null}
    >
      {step === Step.Username && (limitReached?.message || limitReached?.warning) && (
        <div className={'flex flex-col gap-4 mb-4 p-4'}>
          {limitReached?.warning && (
            <p className={'m-0 text-yellow-600 text-center'}>
              <Icon icon={'exclamation-triangle'} /> {limitReached.warning}
            </p>
          )}
          {limitReached?.message && <p className={'m-0 text-center'}>{limitReached.message}</p>}
        </div>
      )}
      {step === Step.OTP && (
        <p className={'mt-0 -mx-6 px-6 py-4 bg-zinc-100 text-center leading-normal'}>
          Please enter the 6-digit code we sent to your email address.
          <br />
          <strong>{username}</strong>
        </p>
      )}
      {step === Step.Username && (
        <Form<FieldValues>
          fields={[
            {
              name: 'email',
              rules: {
                maxLength: {
                  value: 100,
                  message: 'Email cannot exceed 100 characters.',
                },
                pattern: {
                  value: emailRegExp,
                  message: 'Invalid email address. E.g. example@email.com',
                },
                required: 'Email is required.',
              },
              type: 'email',
              typeOptions: {
                autoFocus: true,
                className: 'h-[3rem]',
                placeholder: 'Email',
              },
            },
            {
              name: 'newPassword',
              rules: {
                required: 'Password is required.',
                validate: (value) => validatePassword(value as string),
              },
              type: 'password',
              typeOptions: {
                autoComplete: 'new-password',
                feedback: false,
                id: 'password',
                inputClassName: 'h-[3rem]',
                placeholder: 'Password',
                toggleMask: true,
              },
            },
          ]}
          onSubmit={(fieldValues) => {
            return register({
              ...fieldValues,
              confirmPassword: fieldValues.newPassword,
              name: fieldValues.email,
              username: fieldValues.email,
            })
              .unwrap()
              .then(() => {
                setUsername(fieldValues.email);
                setStep(Step.OTP);
              });
          }}
          render={({renderError, renderFieldByName, renderSubmitButton}) => (
            <div className={'flex flex-col gap-4 w-full'}>
              {renderError()}
              {renderFieldByName('email')}
              {renderFieldByName('newPassword')}
              <div className={'mb-4 leading-none text-sm pl-4 text-muted-foreground'}>
                By clicking Create an account, you agree to our{' '}
                <Link to={`${getConfigValue('landingUrl')}/terms-of-use`}>Terms of Use</Link>.
              </div>
              {renderSubmitButton()}
            </div>
          )}
          submitButton={{
            children: 'Create an account',
            variant: 'success',
          }}
        />
      )}
      {step === Step.OTP && (
        <OTPForm
          cancel={() => setStep(Step.Username)}
          resend={() =>
            registerResendOTP({
              usernameOrEmail: username,
            }).unwrap()
          }
          resendButton={{
            variant: 'default',
          }}
          submitButton={{
            variant: 'success',
          }}
          verify={(otp) =>
            registerVerifyOTP({
              usernameOrEmail: username,
              otp,
            })
              .unwrap()
              .then(() => {
                dispatch(api.util.resetApiState());
                navigate('/welcome');
              })
          }
        />
      )}
      {limitReached?.closable === false && (
        <Button
          className={'text-muted-foreground block mx-auto mt-4'}
          onClick={() => {
            navigate('/sign-in');
            dispatch(logout());
            dispatch(api.util.resetApiState());
          }}
          type={'button'}
          variant="link"
        >
          {'Leave the demo'}
        </Button>
      )}
    </Modal>
  );
};
